import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFeedback from '../../hooks/useFeedback';
import { useAuth } from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const RATING_CONFIG = {
  up: {
    label: 'Daumen hoch',
    color: 'success.main',
    placeholder: 'Was hat dir gefallen?',
    Icon: ThumbUpOutlinedIcon,
  },
  idea: {
    label: 'Idee',
    color: 'warning.main',
    placeholder: 'Welchen Verbesserungsvorschlag hast du?',
    Icon: LightbulbOutlinedIcon,
  },
  down: {
    label: 'Daumen runter',
    color: 'error.main',
    placeholder: 'Was hat dich gestört?',
    Icon: ThumbDownOutlinedIcon,
  },
};

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE');
}

function RatingPill({ rating, selected, onClick }) {
  const theme = useTheme();
  const cfg = RATING_CONFIG[rating];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  const idleBorder =
    theme.palette.mode === 'light' ? theme.palette.divider : alpha(theme.palette.common.white, 0.2);
  const idleColor =
    theme.palette.mode === 'light' ? theme.palette.text.primary : alpha(theme.palette.common.white, 0.8);

  const token = String(cfg.color).split('.');
  const paletteKey = token[0];
  const shadeKey = token[1] || 'main';
  const fillMain =
    theme.palette[paletteKey]?.[shadeKey] != null
      ? theme.palette[paletteKey][shadeKey]
      : theme.palette.primary.main;
  const selectedContrast = theme.palette.getContrastText(fillMain);

  return (
    <Tooltip title={cfg.label}>
      <Button
        variant={selected ? 'contained' : 'outlined'}
        onClick={onClick}
        sx={{
          borderColor: selected ? cfg.color : idleBorder,
          color: selected ? selectedContrast : idleColor,
          backgroundColor: selected ? cfg.color : 'transparent',
          minWidth: 48,
          px: 1.25,
          '&:hover': {
            borderColor: cfg.color,
          },
        }}
      >
        <Icon
          sx={{
            color: selected ? selectedContrast : cfg.color,
          }}
        />
      </Button>
    </Tooltip>
  );
}

const RECENTLY_READ_MS = 60 * 1000;

function isRecentlyRead(item) {
  const readAt = item?.readAt;
  if (!readAt) return false;
  const t = new Date(readAt).getTime();
  return !Number.isNaN(t) && Date.now() - t < RECENTLY_READ_MS;
}

function isFeedbackEditable(item) {
  const status = item?.status || (item?.solved ? 'solved' : 'open');
  return status !== 'solved' && status !== 'rejected';
}

function FeedbackItem({
  item,
  viewLabelByKey,
  isRecentlyRead: highlight,
  onEdit,
  onDelete,
  onReply,
  isDeleting,
}) {
  const theme = useTheme();
  const cfg = RATING_CONFIG[item.rating];
  const Icon = cfg?.Icon;
  const viewLabel = viewLabelByKey[item.viewKey] || item.viewKey || '-';
  const status = item.status || (item.solved ? 'solved' : 'open');
  const isSolved = status === 'solved';
  const isRejected = status === 'rejected';
  const isInProgress = status === 'inProgress';
  const isEditable = isFeedbackEditable(item);

  const messages = Array.isArray(item.messages) ? item.messages : [];
  // The author may reply once a manager has answered (primary response or a
  // previous thread message).
  const hasAdminResponse = Boolean(item.response) || messages.some(m => m.sender === 'admin');

  const replyRef = useRef(null);
  const [replyLen, setReplyLen] = useState(0);
  const [showReply, setShowReply] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);

  const handleReplyChange = useCallback(e => {
    const v = e?.target?.value ?? '';
    setReplyLen(typeof v === 'string' ? v.trim().length : 0);
  }, []);

  const cancelReply = useCallback(() => {
    if (replyRef.current) replyRef.current.value = '';
    setReplyLen(0);
    setShowReply(false);
  }, []);

  const submitReply = useCallback(async () => {
    const text = replyRef.current?.value?.trim() || '';
    if (!text) return;
    setIsSendingReply(true);
    try {
      await onReply?.(item, text);
      cancelReply();
    } catch {
      // error toast handled in the hook
    } finally {
      setIsSendingReply(false);
    }
  }, [onReply, item, cancelReply]);

  return (
    <Paper
      variant='outlined'
      sx={{
        p: { xs: 1, sm: 1.5 },
        ...(highlight && {
          borderColor: 'primary.main',
          borderWidth: 1,
          borderStyle: 'solid',
          backgroundColor: 'action.selected',
        }),
      }}
    >
      <Stack spacing={0.75}>
        <Stack
          direction='column'
          spacing={1}
          alignItems='stretch'
        >
          <Stack
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
          >
            {Icon ? (
              <Icon sx={{ color: cfg.color, fontSize: { xs: '1.1rem', sm: '1.25rem' }, mt: 0.25, flexShrink: 0 }} />
            ) : null}
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant='subtitle2'
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  overflowWrap: 'anywhere',
                }}
              >
                {viewLabel}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              >
                {formatDateTime(item?.meta?.createdAt)}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {isSolved ? (
              <Chip
                size='small'
                icon={<DoneAllIcon />}
                label='Gelöst'
                sx={{ backgroundColor: alpha(theme.palette.success.main, 0.2), color: 'success.main' }}
              />
            ) : isRejected ? (
              <Chip
                size='small'
                icon={<CancelIcon />}
                label='Abgelehnt'
                sx={{ backgroundColor: alpha(theme.palette.error.main, 0.2), color: 'error.main' }}
              />
            ) : isInProgress ? (
              <Chip
                size='small'
                label='In Arbeit'
                sx={{ backgroundColor: alpha(theme.palette.info.main, 0.15), color: 'info.main' }}
              />
            ) : (
              <Chip
                size='small'
                label='Offen'
                sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.15), color: 'warning.main' }}
              />
            )}
            {item?.bookingNumber ? <Chip size='small' label={`Buchung #${item.bookingNumber}`} /> : null}
          </Stack>
        </Stack>
        {item.text ? (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {item.text}
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Kein Text
          </Typography>
        )}
        {item.response && (
          <Paper variant='outlined' sx={{ p: 1, backgroundColor: 'action.hover' }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
              Antwort:
            </Typography>
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}>
              {item.response}
            </Typography>
          </Paper>
        )}
        {messages.length > 0 && (
          <Stack spacing={0.75}>
            {messages.map((msg, idx) => {
              const isAdmin = msg.sender === 'admin';
              return (
                <Paper
                  key={msg._id || idx}
                  variant='outlined'
                  sx={{ p: 1, backgroundColor: isAdmin ? 'action.hover' : 'transparent' }}
                >
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                    {isAdmin ? msg.authorName || 'Team' : msg.authorName || 'Du'}
                    {msg.createdAt ? ` · ${formatDateTime(msg.createdAt)}` : ''}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}
                  >
                    {msg.text}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        )}
        {hasAdminResponse &&
          (showReply ? (
            <Stack spacing={1}>
              <TextField
                label='Deine Antwort'
                placeholder='Antwort schreiben…'
                multiline
                minRows={2}
                maxRows={8}
                inputRef={replyRef}
                defaultValue=''
                onChange={handleReplyChange}
                size='small'
                fullWidth
                helperText={
                  isSolved || isRejected ? 'Deine Antwort öffnet die Anfrage erneut.' : ' '
                }
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent='flex-end'>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={cancelReply}
                  disabled={isSendingReply}
                  fullWidth
                >
                  Abbrechen
                </Button>
                <Button
                  size='small'
                  variant='contained'
                  onClick={() => {
                    submitReply().catch(() => {});
                  }}
                  disabled={isSendingReply || replyLen < 1}
                  fullWidth
                >
                  Senden
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack direction='row' justifyContent='flex-end'>
              <Button size='small' variant='outlined' onClick={() => setShowReply(true)}>
                Antworten
              </Button>
            </Stack>
          ))}
        {isEditable ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent='flex-end'>
            <Button
              size='small'
              variant='outlined'
              startIcon={<EditOutlinedIcon />}
              onClick={() => onEdit?.(item)}
              fullWidth
            >
              Bearbeiten
            </Button>
            <Button
              size='small'
              variant='outlined'
              color='error'
              startIcon={<DeleteOutlineIcon />}
              onClick={() => onDelete?.(item)}
              disabled={isDeleting}
              fullWidth
            >
              Löschen
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function FeedbackView() {
  const authCtx = useAuth();
  const { createFeedback, getMyFeedback, updateMyFeedback, deleteMyFeedback, replyToMyFeedback } =
    useFeedback();

  const [rating, setRating] = useState('idea');
  const [viewKey, setViewKey] = useState('');
  const bookingNumberRef = useRef(null);
  const textRef = useRef(null);
  const [textLen, setTextLen] = useState(0);
  const didInitialLoadRef = useRef(false);

  const [myItems, setMyItems] = useState([]);
  const [isLoadingMine, setIsLoadingMine] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingFeedbackId, setEditingFeedbackId] = useState('');
  const [deletingFeedbackId, setDeletingFeedbackId] = useState('');
  const [feedbackPendingDelete, setFeedbackPendingDelete] = useState(null);

  const viewOptions = useMemo(() => {
    const items = Array.isArray(authCtx.menuItems) ? authCtx.menuItems : [];
    const opts = items
      .filter(i => i && typeof i.link === 'string' && i.link.trim())
      .map(i => {
        const label = i.groupPath ? `${i.groupPath} / ${i.name}` : i.name;
        return { value: i.link, label };
      });
    opts.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    return opts;
  }, [authCtx.menuItems]);

  const viewLabelByKey = useMemo(() => {
    const m = {};
    for (const opt of viewOptions) {
      m[opt.value] = opt.label;
    }
    return m;
  }, [viewOptions]);

  useEffect(() => {
    if (!viewKey && viewOptions.length > 0) {
      setViewKey(viewOptions[0].value);
    }
  }, [viewKey, viewOptions]);

  const loadMine = useCallback(async () => {
    setIsLoadingMine(true);
    try {
      const items = await getMyFeedback({ includeSolved: true, markAsRead: true });
      setMyItems(Array.isArray(items) ? items : []);
      authCtx.refreshCountForMenuItem?.('Feedback geben');
    } finally {
      setIsLoadingMine(false);
    }
  }, [getMyFeedback, authCtx]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    loadMine().catch(() => {});
  }, [loadMine]);

  const placeholder = useMemo(() => {
    return RATING_CONFIG[rating]?.placeholder || 'Dein Feedback';
  }, [rating]);

  const textRequired = rating !== 'up';

  const handleTextChange = useCallback(e => {
    const v = e?.target?.value ?? '';
    setTextLen(typeof v === 'string' ? v.trim().length : 0);
  }, []);

  const resetForm = useCallback(() => {
    setRating('idea');
    if (viewOptions.length > 0) {
      setViewKey(viewOptions[0].value);
    } else {
      setViewKey('');
    }
    setEditingFeedbackId('');
    setTextLen(0);
    if (bookingNumberRef.current) {
      bookingNumberRef.current.value = '';
    }
    if (textRef.current) {
      textRef.current.value = '';
    }
  }, [viewOptions]);

  const startEditing = useCallback(item => {
    setEditingFeedbackId(String(item?._id || ''));
    setRating(item?.rating || 'idea');
    setViewKey(item?.viewKey || '');
    if (bookingNumberRef.current) {
      bookingNumberRef.current.value = item?.bookingNumber || '';
    }
    const text = item?.text || '';
    setTextLen(typeof text === 'string' ? text.trim().length : 0);
    if (textRef.current) {
      textRef.current.value = text;
      textRef.current.focus();
    }
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, []);

  const cancelEditing = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!rating) return;
    if (!viewKey) return;

    const text = textRef.current?.value ?? '';
    const bookingNumber = bookingNumberRef.current?.value ?? '';
    const trimmedLen = typeof text === 'string' ? text.trim().length : 0;
    if (textRequired && trimmedLen < 3) return;

    setIsSubmitting(true);
    try {
      if (editingFeedbackId) {
        await updateMyFeedback({ feedbackId: editingFeedbackId, rating, viewKey, bookingNumber, text });
      } else {
        await createFeedback({ rating, viewKey, bookingNumber, text });
      }
      resetForm();
      await loadMine();
    } finally {
      setIsSubmitting(false);
    }
  }, [createFeedback, editingFeedbackId, loadMine, rating, resetForm, textRequired, updateMyFeedback, viewKey]);

  const submitSafely = useCallback(() => {
    handleSubmit().catch(() => {});
  }, [handleSubmit]);

  const handleDelete = useCallback(
    item => {
      const feedbackId = String(item?._id || '');
      if (!feedbackId) return;
      setFeedbackPendingDelete(item);
    },
    []
  );

  const handleReply = useCallback(
    async (item, text) => {
      const feedbackId = String(item?._id || '');
      const trimmed = typeof text === 'string' ? text.trim() : '';
      if (!feedbackId || !trimmed) return;
      await replyToMyFeedback({ feedbackId, text: trimmed });
      await loadMine();
    },
    [replyToMyFeedback, loadMine]
  );

  const closeDeleteDialog = useCallback(() => {
    if (deletingFeedbackId) return;
    setFeedbackPendingDelete(null);
  }, [deletingFeedbackId]);

  const confirmDelete = useCallback(async () => {
      const feedbackId = String(feedbackPendingDelete?._id || '');
      if (!feedbackId) return;

      setDeletingFeedbackId(feedbackId);
      try {
        await deleteMyFeedback(feedbackId);
        if (feedbackId === editingFeedbackId) {
          resetForm();
        }
        await loadMine();
        setFeedbackPendingDelete(null);
      } finally {
        setDeletingFeedbackId('');
      }
    },
    [deleteMyFeedback, editingFeedbackId, feedbackPendingDelete, loadMine, resetForm]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 1, sm: 2 }, maxWidth: 1100, width: '100%', mx: 'auto' }}>
      <Stack spacing={2}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            <Typography variant='h5' sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {editingFeedbackId ? 'Feedback bearbeiten' : 'Feedback geben'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {editingFeedbackId
                ? 'Du kannst dein Feedback noch anpassen, solange es nicht geloest oder abgelehnt wurde.'
                : 'Gib uns kurz Feedback – das hilft uns, die Oberfläche gezielt zu verbessern.'}
            </Typography>

            <Divider />

            <Stack direction='column' spacing={1.25}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                justifyContent={{ xs: 'stretch', sm: 'flex-start' }}
                flexWrap='wrap'
                useFlexGap
              >
                <RatingPill rating='up' selected={rating === 'up'} onClick={() => setRating('up')} />
                <RatingPill
                  rating='idea'
                  selected={rating === 'idea'}
                  onClick={() => setRating('idea')}
                />
                <RatingPill
                  rating='down'
                  selected={rating === 'down'}
                  onClick={() => setRating('down')}
                />
              </Stack>

              <FormControl size='small' fullWidth sx={{ minWidth: 0 }}>
                <InputLabel id='feedback-view-label'>Reiter</InputLabel>
                <Select
                  labelId='feedback-view-label'
                  label='Reiter'
                  value={viewKey}
                  onChange={e => setViewKey(e.target.value)}
                >
                  {viewOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TextField
              label='Buchungsnummer (optional)'
              placeholder='Buchungsnummer (optional)'
              inputRef={bookingNumberRef}
              defaultValue=''
            />

            <TextField
              label='Freitext'
              placeholder={placeholder}
              multiline
              minRows={3}
              maxRows={10}
              inputRef={textRef}
              defaultValue=''
              onChange={handleTextChange}
              helperText={textRequired && textLen < 3 ? 'Mindestens 3 Zeichen erforderlich' : ' '}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent='flex-end'>
              {editingFeedbackId ? (
                <Button variant='outlined' onClick={cancelEditing} disabled={isSubmitting}>
                  Abbrechen
                </Button>
              ) : null}
              <Button
                variant='contained'
                onClick={submitSafely}
                disabled={isSubmitting || !viewKey || (textRequired && textLen < 3)}
              >
                {editingFeedbackId ? 'Speichern' : 'Senden'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            <Typography variant='h6' sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Mein Feedback
            </Typography>
            {isLoadingMine ? (
              <Typography variant='body2' color='text.secondary'>
                Lädt…
              </Typography>
            ) : myItems.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Noch kein Feedback gesendet.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {myItems.map(item => (
                  <FeedbackItem
                    key={item._id}
                    item={item}
                    viewLabelByKey={viewLabelByKey}
                    isRecentlyRead={isRecentlyRead(item)}
                    onEdit={startEditing}
                    onDelete={handleDelete}
                    onReply={handleReply}
                    isDeleting={deletingFeedbackId === String(item._id)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
      <Dialog
        open={Boolean(feedbackPendingDelete)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Feedback loeschen?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            Soll dieses Feedback wirklich geloescht werden? Dieser Schritt kann nicht rueckgaengig
            gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingFeedbackId)}>
            Abbrechen
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              confirmDelete().catch(() => {});
            }}
            disabled={Boolean(deletingFeedbackId)}
          >
            Loeschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

